import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { DataPerformanceSummaryRequestBodyObjectType, Direction } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import onHoverPopup from 'webapp/modifiers/on-hover-popup';
import { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';
import CommonPopupsAiRegenerateComponent from 'webapp/components/common/popups/ai-regenerate';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type BlockState from 'webapp/models-custom/block-state';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';

import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';

interface Args {
	blockState: BlockState;
	dataPerformanceSummaryObject?: DataPerformanceSummaryObject | null;
	regenerateOrigin?: string;
}

export default class AiRegenerateIconComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('popup') declare popupService: Service.Popup;

	@tracked isRegenerateInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (
			this.currentUserService.isLoggedIn &&
			this.mmDataPerformanceSummariesService.dailyUserLimitRemaining === Infinity
		) {
			// need them to show the limit remaining
			void this.mmDataPerformanceSummariesService.getAndUpdateLimits();
		}
	}

	get blockId() {
		return this.args.blockState.block.numericId;
	}

	get lastSync() {
		return this.args.blockState.block.lastSync;
	}

	get isLimitReached() {
		// check account limits and block status
		return (
			this.mmDataPerformanceSummariesService.limitReached ||
			this.args.blockState.block.aiSummaryStatus === AiSummaryContentStatus.LIMIT_REACHED
		);
	}

	get isDisabled() {
		return this.isLimitReached || this.isRegenerateInProgress || !this.canRegenerateAiSummary;
	}

	get canRegenerateAiSummary() {
		return !this.skipActions && this.isLoggedIn;
	}

	get skipActions() {
		if (this.navigationService.isMobileApp) {
			return true;
		}

		const boardObj = this.args.blockState.block.boardObj;
		if (boardObj) {
			return !boardObj.canCurrentUserEdit;
		}

		const report = this.args.blockState.block.reportSlideFragment?.report;
		if (report) {
			return !report.canCurrentUserEdit;
		}

		return false;
	}

	get isLoggedIn() {
		return this.navigationService.isDatawallApp
			? this.navigationService.isDatawallAndHasAccessToBoard
			: this.currentUserService.isLoggedIn;
	}

	get visualization(): VisualizationAiSummary {
		return this.args.blockState.block.blockSetup.visualization as VisualizationAiSummary;
	}

	get regenerateType() {
		if (this.args.blockState.block.aiSummaryIsEdited) {
			return 'edited datablock';
		} else {
			return 'non-edited datablock';
		}
	}

	@action
	openPopup(el: HTMLElement) {
		const popup = this.popupService.createContext(CommonPopupsAiRegenerateComponent, el, {
			popupClass: 'popup-type-data-status',
			directions: [Direction.UP, Direction.DOWN, Direction.RIGHT, Direction.LEFT],
		});

		return popup.open({
			lastSync: this.lastSync,
			canRegenerateAiSummary: this.canRegenerateAiSummary,
			aiSummaryContentStatus: this.args.blockState.block.aiSummaryStatus,
			editedBy: this.args.blockState.block.aiSummaryEditedBy ?? undefined,
			editedAt: this.args.blockState.block.aiSummaryEditedOn ?? undefined,
		});
	}

	@action
	regenerateAiSummary(event: PointerEvent) {
		event.stopPropagation();

		if (!this.canRegenerateAiSummary || this.isDisabled) {
			return;
		}

		this.visualization.showRegenerationWarning(
			() => {
				void this.executeRegeneration();
			},
			() => {
				// no action on reject
			},
		);
	}

	private executeRegeneration() {
		const dataPerformanceSummaryObject = this.args.dataPerformanceSummaryObject;
		if (dataPerformanceSummaryObject) {
			const summaryModel = this.mmDataPerformanceSummariesService.createModel(
				dataPerformanceSummaryObject,
			);
			this.mmDataPerformanceSummariesService.trackEvent(
				TrackingEvent.DATA_PERFORMANCE_SUMMARY_REFRESHED,
				summaryModel,
				{
					origin: this.args.regenerateOrigin,
					choice: 'datablock',
					block_type: this.regenerateType,
				},
			);
		}

		// regenerate for AI summary block goes to diff endpoint than other GenAI summaries (same payload)
		const blockId = this.blockId;
		this.args.blockState.block.setAiSummaryStatusToLoading();

		const payload = {
			typeObject: DataPerformanceSummaryRequestBodyObjectType.BLOCK,
			typeObjectId: blockId,
			refresh: true,
		};
		return this.requestService
			.api<APIData.GenerativeAI.DataPerformance.SummariesResponse>(
				'PUT',
				`/blocks/${blockId}/generate`,
				payload,
				{ timeout: 60 * 1000 },
			)
			.then((reply) => {
				this.mmDataPerformanceSummariesService.updateLimits(reply?.limits || []);

				this.args.blockState.block.aiSummaryEditedBy = null;
				this.args.blockState.block.aiSummaryEditedOn = null;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<a
			role="button"
			class="ai-regenerate {{if this.isDisabled 'disabled'}}"
			{{on "click" this.regenerateAiSummary}}
			{{onHoverPopup this.openPopup}}
		>
			<CommonUiIconComponent @name="Refresh" @size="dynamic" />
		</a>
	</template>
}
